import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { createEntity } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { createNewUser } from '@adornis/users/server/util.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { ensureAppUserForFieldEngineer } from '@nsc/smartteams-shared/api/create-app-user-for-fe.js';
import { normalizedEmailFilter, normalizeEmail } from '@nsc/smartteams-shared/api/email-filter.js';
import { SmartTeamsFieldEngineer } from '../db/SmartTeamsFieldEngineer.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsPermission } from '../features/permissions/roles-and-permissions.js';
import { sendFieldEngineerOnboardingEmail } from './send-field-engineer-onboarding-email.js';

/**
 * Creates a field engineer along with a user account so they can log in.
 * The user is created with the field engineer's email as both username and email.
 */
export const createFieldEngineerWithUser = registerMutation({
  operationName: 'createFieldEngineerWithUser',
  type: () => String,
  params: [
    { name: 'fieldEngineer', type: () => SmartTeamsFieldEngineer },
    { name: 'password', type: () => String },
  ],
  resolve: (fieldEngineer: SmartTeamsFieldEngineer, password?: string) => async () => {
    const partnerCompanyIds = fieldEngineer.partnerCompanyIds ?? [];
    const permissions = await Promise.all(
      partnerCompanyIds.map(id => accessController.can(createPermissionPayload(SmartTeamsPermission.FECreate, id))),
    );
    if (!permissions.every(Boolean)) {
      throw new Error('No permission to create field engineers for this company');
    }

    let existingUserId: string | undefined;
    if (fieldEngineer.email) {
      fieldEngineer.email = normalizeEmail(fieldEngineer.email);

      // Reject a duplicate field engineer up front — before creating any user account — so a
      // dup email can't leave an orphaned user behind. The FE beforeCreate hook is the backstop.
      // Only *live* records count: a soft-deleted field engineer's email is free to reuse (the
      // partial unique index also excludes `deletedAt != null` rows).
      const feCollection = await getRawCollection<SmartTeamsFieldEngineer>(SmartTeamsFieldEngineer._collectionName);
      const existingFieldEngineer = await feCollection.findOne(
        { ...normalizedEmailFilter(fieldEngineer.email), deletedAt: null },
        { projection: { _id: 1 } },
      );
      if (existingFieldEngineer) {
        throw new ExpectedError(`A field engineer with email ${fieldEngineer.email} already exists.`, {
          translationKey: 'field_engineer.email_in_use',
        });
      }

      // NB: no `deletedAt` filter here on purpose. A live orphaned user (its FE was deleted) should
      // be reused so the person keeps their login. We can't route to a fresh account instead,
      // because @adornis' createNewUser rejects on a case-insensitive email/username match that
      // ignores deletedAt — so a soft-deleted user's email can't be handed to a brand-new account.
      const userCollection = await getRawCollection<AdornisUser>(AdornisUser._collectionName);
      const matchingUsers = await userCollection
        .find(normalizedEmailFilter(fieldEngineer.email), { projection: { _id: 1 } })
        .limit(2)
        .toArray();
      if (matchingUsers.length > 1) {
        throw new ExpectedError(
          `Duplicate user accounts differing only in case exist for ${fieldEngineer.email} and must be merged manually`,
        );
      }
      existingUserId = matchingUsers[0]?._id;
    }

    // If no password provided, create field engineer without user account
    if (!password || existingUserId) {
      const fieldEngineerData = new SmartTeamsFieldEngineer({
        partnerCompanyIds: fieldEngineer.partnerCompanyIds,
        name: fieldEngineer.name,
        email: fieldEngineer.email,
        externalId: fieldEngineer.externalId,
        serviceNowSysId: fieldEngineer.serviceNowSysId,
        defaultLocation: fieldEngineer.defaultLocation,
        firstLanguage: fieldEngineer.firstLanguage,
        secondLanguage: fieldEngineer.secondLanguage,
        userId: existingUserId,
        mustChangePassword: existingUserId ? true : false,
      });
      const fieldEngineerId = await createEntity(fieldEngineerData)();
      if (existingUserId) {
        await ensureAppUserForFieldEngineer({
          userId: existingUserId,
          email: fieldEngineer.email!,
          primaryLanguage: fieldEngineer.firstLanguage ?? undefined,
          serviceNowFEId: fieldEngineer.serviceNowSysId ?? undefined,
          companyId: fieldEngineer.partnerCompanyIds?.[0],
        });
        // The FE + app user are already committed; don't let a mail/permission failure abort and
        // leave a half-created account (mirrors the per-row guard in the CSV importer).
        try {
          await sendFieldEngineerOnboardingEmail(fieldEngineerId)();
        } catch (error: unknown) {
          logger.warn({ fieldEngineerId, error }, 'Field engineer created, but onboarding email failed');
        }
      }
      return fieldEngineerId;
    } else {
      // Require email to create a user account
      if (!fieldEngineer.email) {
        throw new Error('Email is required to create a user account for the field engineer');
      }

      // Backoffice creation: enforce the password policy here (createNewUser does not validate).
      // Onboarding email / CSV import deliberately skip this — they accept temporary passwords.
      AdornisUser.passwordValidator({ value: password, key: 'password', target: undefined as never });

      // Create the user first - use email as username
      const username = fieldEngineer.email;
      const user = new AdornisUser({ username, email: fieldEngineer.email });

      const userId = await createNewUser(user, password);

      // Create the field engineer with the userId linked
      const fieldEngineerData = new SmartTeamsFieldEngineer({
        partnerCompanyIds: fieldEngineer.partnerCompanyIds,
        name: fieldEngineer.name,
        email: fieldEngineer.email,
        externalId: fieldEngineer.externalId,
        serviceNowSysId: fieldEngineer.serviceNowSysId,
        defaultLocation: fieldEngineer.defaultLocation,
        firstLanguage: fieldEngineer.firstLanguage,
        secondLanguage: fieldEngineer.secondLanguage,
        userId,
        mustChangePassword: true,
      });

      const fieldEngineerId = await createEntity(fieldEngineerData)();

      await ensureAppUserForFieldEngineer({
        userId,
        email: fieldEngineer.email!,
        primaryLanguage: fieldEngineer.firstLanguage ?? undefined,
        serviceNowFEId: fieldEngineer.serviceNowSysId ?? undefined,
        companyId: fieldEngineer.partnerCompanyIds?.[0],
      });
      try {
        await sendFieldEngineerOnboardingEmail(fieldEngineerId, password)();
      } catch (error: unknown) {
        logger.warn({ fieldEngineerId, error }, 'Field engineer created, but onboarding email failed');
      }

      return fieldEngineerId;
    }
  },
});
